// /super-admin/settings — central landing for super-admin-only knobs
// that don't fit on /super-admin (which is company management). Right
// now this hosts the scheduler controls panel: a quick deep-link into
// the Automation Pipeline page's Schedulers tab + an explainer of the
// env knobs (DUE_POST_INTERVAL_MS, DUE_POST_BATCH) for the things that
// need a worker restart instead of a hot-swap.
//
// Architecture choice: the runtime controls (Run now, Set interval)
// live on /automation-pipeline so there's a single source of truth.
// This page just makes them discoverable + documents the boundary
// between "runtime hot-swap" and "env-var + restart".

import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, Redirect, Link } from 'react-router-dom';
import LlmProviderPanel from '../AutomationPipeline/LlmProviderPanel';
import LlmBrandProviderPicker from '../AutomationPipeline/LlmBrandProviderPicker';

const Section = ({ title, children }) => (
  <div style={{
    background: '#fff', border: '1px solid #e5e5e5', borderRadius: 6,
    padding: 18, marginBottom: 16,
  }}>
    <h6 style={{ marginBottom: 12, fontWeight: 600 }}>{title}</h6>
    {children}
  </div>
);

const Code = ({ children }) => (
  <code style={{
    background: '#f4f4f4', padding: '2px 6px', borderRadius: 3,
    fontSize: 12, fontFamily: 'Menlo, Monaco, monospace',
  }}>{children}</code>
);

export default function SuperAdminSettings() {
  const isSuperAdmin = useSelector((s) => !!s.user.userInfo?.superAdmin);
  const history = useHistory();
  // Strictly super-admin. The proxy already enforces this on every
  // /worker/api/schedulers/* call, but we should never even render the
  // page to a non-super.
  if (!isSuperAdmin) return <Redirect to="/dashboard" />;

  return (
    <div className="personal-area" style={{ padding: '24px 32px' }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
          <Link to="/super-admin" style={{ color: '#888' }}>Super admin</Link>
          {' › '}
          <span>Settings</span>
        </div>
        <h4 style={{ margin: 0 }}>Super admin settings</h4>
        <p style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
          Process-level knobs that affect every tenant. Anything here that's
          gated to super-admin only is also enforced by the worker proxy.
        </p>
      </div>

      <Section title="LLM providers">
        <p style={{ fontSize: 13, color: '#444', marginBottom: 12 }}>
          Pick the global default LLM provider, see each provider's live
          “ready to use” key status, edit any provider's API key (saved to the
          worker and applied immediately), and watch the SiliconFlow balance.
          Per-brand provider overrides live on{' '}
          <Link to="/automation-pipeline">Automation Pipeline → ▶ Run flows</Link>.
        </p>
        <LlmProviderPanel />
      </Section>

      <Section title="Per-brand provider override">
        <p style={{ fontSize: 13, color: '#444', marginBottom: 12 }}>
          Pin a specific LLM provider per brand — its automated flows lead with
          that provider instead of the global default. “Inherit” clears the pin.
          Only providers with a valid key can be selected.
        </p>
        <LlmBrandProviderPicker />
      </Section>

      <Section title="Scheduler controls">
        <p style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>
          Every periodic worker (due-post, queue-janitor, brand-enrichment,
          email-inbox-poller, …) registers itself with{' '}
          <Code>WorkerPoolManager</Code>. The schedulers panel on the
          Automation Pipeline page lets you:
        </p>
        <ul style={{ fontSize: 13, color: '#444', paddingLeft: 18, marginBottom: 12 }}>
          <li><strong>Run now</strong> — fires a tick out-of-band. Use this when you
              just manually approved a post and don't want to wait up to 60s
              for the next <Code>due-post</Code> sweep.</li>
          <li><strong>Cadence (hot-swap)</strong> — edit the seconds box and click Save
              to change a scheduler's tick interval at runtime. The change is
              <strong> in-memory only</strong> and reverts on the next worker
              restart — for permanent changes use the env knobs below.</li>
        </ul>
        <button
          className="btn"
          onClick={() => history.push('/automation-pipeline?tab=schedulers')}
          style={{
            padding: '8px 14px', fontSize: 13, background: '#15172b',
            color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer',
          }}
        >
          Open scheduler controls →
        </button>
      </Section>

      <Section title="Permanent overrides (env vars + restart)">
        <p style={{ fontSize: 13, color: '#444', marginBottom: 8 }}>
          The runtime hot-swap above resets on every worker restart. For
          changes that should survive restarts, edit the worker&apos;s{' '}
          <Code>.env</Code> and reload the service. Path:{' '}
          <Code>/var/www/Deployment/prigmar-worker/.env</Code> on the
          production host (or <Code>/var/www/development/...</Code> for
          dev).
        </p>
        <table style={{ width: '100%', fontSize: 12, marginTop: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e5e5' }}>
              <th style={{ textAlign: 'left',  padding: 6, fontSize: 11, color: '#888' }}>Variable</th>
              <th style={{ textAlign: 'left',  padding: 6, fontSize: 11, color: '#888' }}>Effect</th>
              <th style={{ textAlign: 'right', padding: 6, fontSize: 11, color: '#888' }}>Default</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}><Code>DUE_POST_INTERVAL_MS</Code></td>
              <td style={{ padding: 6 }}>
                How often <Code>due-post</Code> ticks and sweeps the posts in
                <Code> approved </Code>/<Code> scheduled </Code> whose
                <Code> scheduled_at </Code> has passed.
              </td>
              <td style={{ padding: 6, textAlign: 'right' }}>60000 (60s)</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}><Code>DUE_POST_BATCH</Code></td>
              <td style={{ padding: 6 }}>
                Max posts emitted per due-post tick. Raise if you ever have
                &gt;100 posts coming due in the same minute.
              </td>
              <td style={{ padding: 6, textAlign: 'right' }}>100</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}><Code>GEMINI_API_KEY</Code></td>
              <td style={{ padding: 6 }}>
                Free-tier Google AI Studio key for <strong>image generation</strong>{' '}
                (Gemini 2.5 Flash Image / &quot;nano-banana&quot;). When set, it
                leads the image-provider order and uses the brand&apos;s own
                images as style references; falls back to Replicate / OpenAI if
                unset or rate-limited. Status shows in the API-key audit on the
                Admin Cost Control page. Optional knobs:{' '}
                <Code>GEMINI_IMAGE_MODEL</Code>, <Code>IMAGE_REFERENCE_MAX</Code>.
              </td>
              <td style={{ padding: 6, textAlign: 'right' }}>—</td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}><Code>SILICONFLOW_API_KEY</Code></td>
              <td style={{ padding: 6 }}>
                <strong>Default LLM provider</strong> for text, vision and image
                (OpenAI-compatible aggregator — Qwen / DeepSeek / FLUX). Uses the
                international <Code>.com</Code> host (<Code>SILICONFLOW_BASE_URL=https://api.siliconflow.com/v1</Code>).
                Model knobs: <Code>SILICONFLOW_MODEL</Code> (text),{' '}
                <Code>SILICONFLOW_VISION_MODEL</Code>, <Code>SILICONFLOW_IMAGE_MODEL</Code>,{' '}
                <Code>SILICONFLOW_IMAGE_EDIT_MODEL</Code>. With{' '}
                <Code>LLM_PROVIDER_MODE=auto</Code>, flows run on it and fall back to
                the Claude operator only if every provider fails.
              </td>
              <td style={{ padding: 6, textAlign: 'right' }}>—</td>
            </tr>
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>
          <strong>Provider list + live "ready to use" key status + inline key
          editing for every LLM/image/video provider</strong> now live on{' '}
          <Code>/automation-pipeline</Code> → <Code>▶ Run flows</Code> → the{' '}
          <strong>🧠 LLM provider</strong> panel, where you can also pick the
          provider each brand's flows lead with. See the Documentation page
          (Super-Admin guide § 1.5) for the full matrix.
        </p>
        <p style={{ fontSize: 12, color: '#888', marginTop: 12 }}>
          After editing <Code>.env</Code> on the host:{' '}
          <Code>systemctl restart prigmarworker.service</Code> (prod) or{' '}
          <Code>systemctl restart preprigmarworker.service</Code> (dev).
        </p>
      </Section>

      <Section title="Where things live">
        <table style={{ width: '100%', fontSize: 12 }}>
          <tbody>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}>Scheduler factory + tick loop</td>
              <td style={{ padding: 6, fontFamily: 'Menlo, Monaco, monospace' }}>
                prigmar-worker/schedulers/duePostScheduler.js
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}>Scheduler registry + Run-now / Set-interval</td>
              <td style={{ padding: 6, fontFamily: 'Menlo, Monaco, monospace' }}>
                prigmar-worker/workers/WorkerPoolManager.js
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}>HTTP endpoints (run-now, set-interval, pause, resume)</td>
              <td style={{ padding: 6, fontFamily: 'Menlo, Monaco, monospace' }}>
                prigmar-worker/routes/index.js (§ schedulers)
              </td>
            </tr>
            <tr style={{ borderBottom: '1px solid #f4f4f4' }}>
              <td style={{ padding: 6 }}>Super-admin gating on the proxy</td>
              <td style={{ padding: 6, fontFamily: 'Menlo, Monaco, monospace' }}>
                prigmar-app/back/routes/workerProxy.js (SUPERADMIN_ONLY)
              </td>
            </tr>
            <tr>
              <td style={{ padding: 6 }}>Long-form docs (live view)</td>
              <td style={{ padding: 6 }}>
                <Link to="/admin/documentation">
                  /admin/documentation
                </Link>
                {' '}→ <em>Super-admin dashboard guide</em> § 3.1 — served
                straight from <Code>docs/SUPER_ADMIN.md</Code> on each
                request, so it&apos;s always current with the deployed
                tree.
              </td>
            </tr>
          </tbody>
        </table>
      </Section>
    </div>
  );
}
